import {
    IonButton,
    IonButtons,
    IonContent,
    IonItem,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import useAllUsers from "Hooks/useAllUsers";
import { Alert, Modal, Select, Button } from "antd";
import { Conversation } from "frontend-lib/DomainTypes/Conversation";
import { Agent } from "frontend-lib/DomainTypes/User";
import { isMobileView } from "frontend-lib/Utils/Mobile";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "Requests/Api";
import { useAppContext } from "../../State/Global/AppContext";

const { Option } = Select;
type Props = {
    selectedChat: Conversation;
    visible: boolean;
    handleCloseModal: () => void;
};

export default function DerivateModal({ visible, selectedChat, handleCloseModal }: Props) {
    const [users] = useAllUsers();
    const [selectedAgentId, setSelectedAgentId] = useState<number>();
    const [showChangeAgentRemoveGroupWarning, setShowChangeAgentRemoveGroupWarning] =
        useState(false);
    const [selectedDerivationGroupId, setSelectedDerivationGroupId] = useState<number>();
    const [acceptButtonDisabled, setAcceptButtonDisabled] = useState(true);
    const [derivationHasToSelectGroup, setDerivationHasToSelectGroup] = useState(false);

    //@ts-ignore
    const { actions, appState } = useAppContext();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const selectedChatId = useMemo(() => {
        return appState.selectedChat.id;
    }, [appState.selectedChat.id]);

    const currentAgentId = useMemo(() => {
        return appState.agent.id;
    }, [appState.agent.id]);

    const handleModalOk = async (selectedAgentId: number) => {
        let updateData: {
            markAsOwnedByUserId: number;
            chatGroups: Array<{ id: number }> | undefined;
        } = {
            markAsOwnedByUserId: selectedAgentId,
            chatGroups: undefined,
        };

        if (selectedDerivationGroupId) {
            const allGroupsId = [
                ...selectedChat.groups.map((group) => group.id),
                selectedDerivationGroupId,
            ];
            updateData.chatGroups = allGroupsId.map((groupId) => {
                return { id: groupId };
            });
        } else if (showChangeAgentRemoveGroupWarning) {
            updateData.chatGroups = [];
            setShowChangeAgentRemoveGroupWarning(false);
        }
        try {
            setIsLoading(true);
            await api.chats().updateChat(selectedChat.id, updateData);

            if (selectedChatId === selectedChat.id && selectedAgentId !== currentAgentId) {
                actions.setSelectedChat({});
            }
            handleClose();
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectAgentChange = (selectedAgentId: number | undefined) => {
        if (selectedAgentId === undefined) return;

        setSelectedAgentId(selectedAgentId);
        setSelectedDerivationGroupId(undefined);

        const agentGroupsId = users
            .find((user) => user.id === selectedAgentId)!
            .chatGroups.map((group) => group.id);
        const chatGroupsId = selectedChat?.groups.map((group: any) => group.id);
        const intersection = agentGroupsId.filter((value) => chatGroupsId.includes(value));
        const thereIsNotAtLeastOnGroupInCommon = intersection.length === 0;

        if (
            chatGroupsId.length > 0 &&
            getUserById(selectedAgentId)!.chatGroups.length > 0 &&
            thereIsNotAtLeastOnGroupInCommon
        ) {
            // Enters here if selected chat has at least one group and selected user has at least one group
            // but there is not groups in common
            setAcceptButtonDisabled(true);
            setDerivationHasToSelectGroup(true);
            setShowChangeAgentRemoveGroupWarning(false);
        } else if (agentGroupsId.length === 0 && chatGroupsId.length > 0) {
            // Enters here if selected chat has at least one group and selected user does not have any group
            setAcceptButtonDisabled(false);
            setDerivationHasToSelectGroup(false);
            setShowChangeAgentRemoveGroupWarning(true);
        } else {
            setAcceptButtonDisabled(false);
            setDerivationHasToSelectGroup(false);
            setShowChangeAgentRemoveGroupWarning(false);
        }
    };

    const getUserById = (userId: number): Agent | undefined => {
        return users.find((user) => user.id === userId);
    };

    const handleSelectDerivationGroupChange = (selectedGroupId: number) => {
        setSelectedDerivationGroupId(selectedGroupId);
        setAcceptButtonDisabled(false);
    };

    const handleClose = () => {
        setSelectedAgentId(undefined);
        setSelectedDerivationGroupId(undefined);
        setAcceptButtonDisabled(true);
        setDerivationHasToSelectGroup(false);
        handleCloseModal();
    };

    const renderChangeGroupAlert = () => {
        return (
            <Alert
                style={{ marginTop: "5px" }}
                message={t("filters.removeGroupsWarning", {
                    agentLabel: getUserById(selectedAgentId!)?.email,
                })}
                type="info"
            />
        );
    };

    if (isMobileView()) {
        return (
            <IonModal isOpen={visible}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton onClick={handleClose}>{t("close")}</IonButton>
                    </IonButtons>
                    <IonTitle>{t("modal.changeChatOwnerTitleReduced")}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            disabled={acceptButtonDisabled}
                            strong={true}
                            onClick={() => handleModalOk(selectedAgentId!)}
                        >
                            {t("accept")}
                            {isLoading && <IonSpinner />}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent>
                    <IonItem>
                        <IonSelect
                            label={t("filters.selectAnAgent")}
                            placeholder={t("select")}
                            value={selectedAgentId}
                            onIonChange={(e) => handleSelectAgentChange(e.detail.value)}
                        >
                            {users.map((u) => (
                                <IonSelectOption value={u.id}>{u.email}</IonSelectOption>
                            ))}
                        </IonSelect>
                    </IonItem>
                    {selectedAgentId && derivationHasToSelectGroup && (
                        <IonItem>
                            <IonSelect
                                label={t("filters.selectGroups")}
                                value={selectedDerivationGroupId}
                                onIonChange={(e) =>
                                    handleSelectDerivationGroupChange(e.detail.value)
                                }
                                placeholder={t("select")}
                            >
                                {getUserById(selectedAgentId)!.chatGroups.map((group) => (
                                    <IonSelectOption value={group.id}>{group.name}</IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonItem>
                    )}
                    {showChangeAgentRemoveGroupWarning && renderChangeGroupAlert()}
                </IonContent>
            </IonModal>
        );
    }
    return (
        <Modal
            open={visible}
            title={t("modal.changeChatOwnerTitle")}
            footer={[
                <Button key="back" onClick={handleClose}>
                    {t("cancel")}
                </Button>,
                <Button
                    loading={isLoading}
                    disabled={acceptButtonDisabled}
                    key="submit"
                    type="primary"
                    onClick={() => handleModalOk(selectedAgentId!)}
                >
                    {t("accept")}
                </Button>,
            ]}
            onCancel={handleClose}
        >
            <Select
                value={selectedAgentId}
                defaultActiveFirstOption={false}
                onChange={handleSelectAgentChange}
                style={{ width: "100%", backgroundColor: "red" }}
                placeholder={t("filters.selectAnAgent")}
                allowClear={true}
                showSearch={true}
                filterOption={(input, option: any) =>
                    option.props.children.join().toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
            >
                {users.map((user) => (
                    <Option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}: {user.email}
                    </Option>
                ))}
            </Select>
            {selectedAgentId && derivationHasToSelectGroup && (
                <Select
                    value={selectedDerivationGroupId}
                    defaultActiveFirstOption={false}
                    onChange={handleSelectDerivationGroupChange}
                    style={{ width: "100%", marginTop: "20px" }}
                    placeholder={t("filters.selectGroups")}
                    showSearch={true}
                    filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {getUserById(selectedAgentId)!.chatGroups.map((group) => (
                        <Option key={group.id} value={group.id}>
                            {group.name}
                        </Option>
                    ))}
                </Select>
            )}
            {showChangeAgentRemoveGroupWarning && renderChangeGroupAlert()}
        </Modal>
    );
}
